import React, { Fragment, useEffect, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import moment from 'moment';
import PropTypes from 'prop-types';

import { requestInsert } from 'api/sfms/exchangeRate';
import { requestList as requestCode } from 'api/standard/code';
import Select from 'components/common-sfims/form/Select';
import Modal from 'components/common-sfims/modal/Common';
import { array2ValueAndText } from 'function/convert';
import { onEnterKeyPress, onInputChange } from 'function/event';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import format from 'utils/string/format';

/**
 * 배합비 작성 정보 관리 > 원료 가격 - 환율 등록/수정
 */
const Setting = ({ show, inform, onHide, onSetting }) => {
  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  // 국가 목록
  const [countries, setCountries] = useState([]);
  // 환율 등록/수정 요청 데이터
  const [exchangeRateData, setExchangeRateData] = useObjReducer({
    exchangeYm: '',
    nationCode: '',
    rate: ''
  });
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우
    if (!show) {
      return;
    }

    // 국가 목록 조회 요청
    requestCountries();

    // 데이터 초기화
    setExchangeRateData({
      exchangeYm: inform.exchangeYm,
      nationCode: '',
      rate: ''
    });
  }, [show]);

  /* method */
  // 국가 목록 조회 요청
  const requestCountries = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'country' });

    setCountries(list);
  };

  /* event */
  // 설정
  const onSubmit = async () => {
    if (isDisableSetting) {
      return;
    }

    let title = 'insert';
    let message = 'completeInsertMessage';

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(exchangeRateData)) {
        throw { code: RESPONSE_CODE.BadRequestInsert };
      }

      const { code, message } = await requestInsert({
        ...exchangeRateData,
        rate: Number(exchangeRateData.rate.replaceAll(',', ''))
      });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      // 설정 시 이벤트 실행
      onSetting();

      // 모달 숨김
      onHide();
    } catch (error) {
      title = 'notice';
      message = getErrorMessage(error?.code, error?.error);
    } finally {
      setConfirmModal({
        isOpen: true,
        title,
        message
      });
      setIsDisableSetting(false);
    }
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{ footer: 'align-items-center justify-content-center' }}
      onHide={onHide}
    >
      <Fragment key="header">
        {format(getText('monthlyExchangeRate'), {
          year: moment(inform.exchangeYm).year(),
          month: moment(inform.exchangeYm).month() + 1
        })}
      </Fragment>
      <Fragment key="body">
        <Form.Group>
          <InputGroup>
            <Select
              datas={array2ValueAndText(countries, 'codeCd', 'codeNm')}
              placeholder="nation"
              value={exchangeRateData.nationCode}
              name="nationCode"
              onChange={event => onInputChange(event, setExchangeRateData)}
            />
            <Form.Control
              size="sm"
              type="text"
              value={exchangeRateData.rate}
              autoFocus
              className="text-end fs--1 fw-bold w-50"
              name="rate"
              onChange={event => {
                onInputChange(event, setExchangeRateData, {
                  type: 'number',
                  format: ',0.##',
                  max: 99999.99
                });
              }}
              onKeyUp={event => onEnterKeyPress(event, onSubmit)}
            />
          </InputGroup>
        </Form.Group>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText('cancel')}
        </Button>
        <Button disabled={isDisableSetting} className="ms-2" onClick={onSubmit}>
          {getText('ok')}
        </Button>
      </Fragment>
    </Modal>
  );
};

Setting.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 환율 정보
  inform: PropTypes.object,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 설정 시 이벤트
  onSetting: PropTypes.func
};
Setting.defaultProps = {
  show: false,
  inform: {}
};

export default Setting;
